import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from './../axiosInstance.js';
import Datetime from 'react-datetime';
import moment from 'moment';
import "moment/locale/ru"; // Import the Russian locale
import "react-datetime/css/react-datetime.css";
import "./Logbook.css"; // Import custom CSS for styling
import { Button, Paper, TextInput } from "@mantine/core";

moment.locale("ru");

const Logbook = ({ art, initialDateTimes, language, releaseDate: releaseDateProp }) => {
  const [dates, setDates] = useState(initialDateTimes || []);
  const [currentValue, setCurrentValue] = useState("");
  const [currentValueNote, setCurrentValueNote] = useState("");
  const [finished, setFinished] = useState(art.category === 1);
  const [activeQuickDate, setActiveQuickDate] = useState(undefined); // undefined=nothing chosen yet, 0=now, 1, 4, 24, "zero", null=manual
  const [pickDateWarning, setPickDateWarning] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const inputElement = useRef(null);
  const refs = useRef({});

  // "Release date" quick-date option (movies + games where available).
  // Format: "YYYY-MM-DD". null = still loading / unknown; empty = the
  // backend couldn't produce anything for this art. Uses the parent's
  // prefetched value when supplied (ArtPage does this so the option is
  // visible the instant the Logbook opens), otherwise fetches locally.
  const [releaseDate, setReleaseDate] = useState(releaseDateProp ?? null);
  useEffect(() => {
    if (releaseDateProp !== undefined) { setReleaseDate(releaseDateProp); return; }
    if (art.category !== 1 && art.category !== 2) return;
    let cancelled = false;
    axiosInstance
      .get(`/getArtReleaseDate?artId=${art.id}`)
      .then((res) => { if (!cancelled) setReleaseDate(res.data?.releaseDate || ''); })
      .catch(() => { if (!cancelled) setReleaseDate(''); });
    return () => { cancelled = true; };
  }, [art.id, art.category, releaseDateProp]);

  useEffect(() => {
    if (inputElement.current) {
      inputElement.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [currentValue, cursorPosition]);

  const onInput = () => {
    if (inputElement.current) {
      setCursorPosition(inputElement.current.selectionStart);
    }
  };

  const isDuplicateDate = (newDate) => {
    return dates.some(
      (date) =>
        moment(newDate).format("YYYY-MM-DD HH:mm") ===
        moment(date).format("YYYY-MM-DD HH:mm")
    );
  };

  const showPopup = (message) => {
    alert(message);
  };

  const handleAddDate = () => {
    if (activeQuickDate === undefined) {
      setPickDateWarning(true);
      return;
    }
    const parsedDate = moment(currentValue, ["DD.MM.YYYY HH:mm", "D MMM YYYY HH:mm"], true);
    if (!parsedDate.isValid()) {
      showPopup(language === "ru" ? "Выберите дату" : "Select date");
      return;
    }
    const newDate = parsedDate.toISOString();
    if (isDuplicateDate(newDate)) {
      showPopup(language === "ru" ? "Вы уже добавили такую же дату" : "You already added the same date");
      return;
    }
    const payload = { date: newDate, note: currentValueNote };
    if (art.category === 1 || art.category === 2 || art.category === 3) {
      payload.finished = finished;
    }
    axiosInstance
      .post(`/art/logbook/${art.id}/add`, payload)
      .then((response) => {
        setDates([response.data, ...dates]);
        setCurrentValue("");
        setCurrentValueNote("");
        setFinished(art.category === 1);
        setActiveQuickDate(undefined);
      })
      .catch((error) => {
        console.error("Failed to add date:", error);
      });
  };

  useEffect(() => {
    setFinished(art.category === 1);
  }, [art.category]);

  const handleQuickAddDate = (key) => {
    let newDate;
    if (key === "zero") {
      newDate = new Date(0);
    } else if (key === "release") {
      if (!releaseDate) return;
      // Anchor at noon local so tz shifts don't flip the day.
      newDate = moment(`${releaseDate} 12:00`, "YYYY-MM-DD HH:mm").toDate();
    } else {
      newDate = new Date();
      newDate.setHours(newDate.getHours() - key);
    }
    setCurrentValue(moment(newDate).format("DD.MM.YYYY HH:mm"));
    setActiveQuickDate(key);
    setPickDateWarning(false);
  };

  return (
    <div className="mt-1 w-full">
      <Paper withBorder p="md" radius="md">
        <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1">
          {[
            { key: 0, ru: "Сейчас", en: "Now" },
            { key: 1, ru: "Час назад", en: "1h ago" },
            { key: 4, ru: "4ч назад", en: "4h ago" },
            { key: 24, ru: "Вчера", en: "Yesterday" },
            { key: "zero", ru: "Давно", en: "Long ago" },
            ...((art.category === 1 || art.category === 2) && releaseDate
              ? [{ key: "release", ru: "Дата премьеры", en: "Release date" }]
              : []),
          ].map(item => {
            const isActive = activeQuickDate === item.key;
            const underline = isActive || pickDateWarning;
            const color = isActive
              ? 'var(--mantine-color-blue-text)'
              : pickDateWarning
                ? 'var(--mantine-color-red-text)'
                : undefined;
            return (
              <span
                key={item.key}
                className={`cursor-pointer text-sm ${underline ? 'underline decoration-solid' : ''} ${isActive ? 'font-medium' : ''}`}
                style={{ color, textDecorationColor: color }}
                onClick={() => handleQuickAddDate(item.key)}
              >
                {language === "ru" ? item.ru : item.en}
              </span>
            );
          })}
        </div>
        {pickDateWarning && (
          <div className="text-center text-xs mb-3" style={{ color: 'var(--mantine-color-red-text)' }}>
            {language === "ru" ? "Сначала выберите дату и время" : "Please select the date and time first"}
          </div>
        )}
        {activeQuickDate !== undefined && (
          <>
            <div className="flex justify-center items-center mb-3">
              {new Date(currentValue).getTime() === 0 ? (
                <span className="text-lg font-medium">
                  {language === "ru" ? "Давно" : "Long time ago"}
                </span>
              ) : (
                <div className="w-[260px]">
                <Datetime
                  locale={language}
                  value={currentValue}
                  onChange={(input) => {
                    setActiveQuickDate(null);
                    if (typeof input === "string") {
                      const parsedDate = moment(input, "DD.MM.YYYY", true);
                      if (parsedDate.isValid()) {
                        setCurrentValue(parsedDate.format("DD.MM.YYYY 00:00"));
                      } else {
                        setCurrentValue(input);
                      }
                    } else if (moment.isMoment(input) && input.isValid()) {
                      setCurrentValue(input.format("DD.MM.YYYY HH:mm"));
                    } else {
                      setCurrentValue("");
                    }
                  }}
                  inputProps={{
                    onInput,
                    ref: inputElement,
                  }}
                  dateFormat="DD.MM.YYYY"
                  timeFormat="HH:mm"
                  className="dark-datetime-picker"
                />
                </div>
              )}
            </div>
            <div className="flex justify-center mb-3">
              <TextInput
                placeholder={language === "ru" ? "Заметка (необязательно)" : "Note (optional)"}
                value={currentValueNote}
                onChange={(e) => setCurrentValueNote(e.target.value)}
                maxLength={100}
                size="sm"
                w={260}
                styles={{ input: { textAlign: 'center' } }}
              />
            </div>
          </>
        )}
      </Paper>
      <div className="flex justify-center mt-3">
        <Button
          variant="filled"
          size="sm"
          onClick={handleAddDate}
        >
          {language === "ru" ? "Добавить дату" : "Add date"}
        </Button>
      </div>
      {dates.length > 0 && (
        <div className="mt-6">
          <div className="text-center font-semibold mb-2" style={{ color: 'var(--mantine-color-dimmed)' }}>
            {language === "ru" ? "Добавленные даты" : "Added dates"}
          </div>
          <div className="space-y-2">
            {dates.map((dateTime) => {
              if (!refs.current[dateTime.id]) {
                refs.current[dateTime.id] = React.createRef();
              }
              return (
                <Paper key={dateTime.id} withBorder radius="md" p="sm" style={{ position: 'relative' }}>
                  <div className="text-center">
                    <div className="text-sm font-medium">
                      {new Date(dateTime.logTime).getTime() === 0
                        ? (language === "ru" ? "Давно" : "Long time ago")
                        : moment(dateTime.logTime).format("DD MMM YYYY, HH:mm")}
                    </div>
                    <input
                      type="text"
                      ref={refs.current[dateTime.id]}
                      className="text-xs w-full text-center mt-1 p-1 rounded focus:outline-none"
                      style={{ backgroundColor: 'transparent', color: 'var(--mantine-color-dimmed)', border: 'none' }}
                      value={dateTime.note || ""}
                      maxLength={100}
                      placeholder={language === "ru" ? "заметка..." : "note..."}
                      onChange={(e) => {
                        const updatedNote = e.target.value;
                        setDates(dates.map((d) =>
                          d.id === dateTime.id ? { ...d, note: updatedNote } : d
                        ));
                      }}
                      onBlur={() => {
                        axiosInstance
                          .post(`/art/logbook/${dateTime.id}/update`, { note: dateTime.note || "" })
                          .catch((error) => console.error("Failed to update note:", error));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          refs.current[dateTime.id].current.blur();
                        }
                      }}
                    />
                  </div>
                  <span
                    className="cursor-pointer flex items-center justify-center"
                    style={{ position: 'absolute', top: 8, right: 8, color: 'var(--mantine-color-red-text)' }}
                    onClick={() => {
                      const formattedDate = new Date(dateTime.logTime).getTime() === 0
                        ? (language === "ru" ? "Давно" : "Long time ago")
                        : moment(dateTime.logTime).format("DD MMM YYYY HH:mm");
                      const confirmMessage = language === "ru"
                        ? `Удалить дату: ${formattedDate}?`
                        : `Delete date: ${formattedDate}?`;
                      if (window.confirm(confirmMessage)) {
                        axiosInstance
                          .delete(`/art/logbook/${dateTime.id}/remove`)
                          .then(() => setDates(dates.filter((d) => d.id !== dateTime.id)))
                          .catch((error) => console.error("Failed to remove date:", error));
                      }
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </span>
                </Paper>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Logbook;
